import { useCallback, useEffect, useRef, useState } from 'react';
import { getFileUploadConfig } from '../../config/fileUpload';
import { useAttachments } from '../../hooks';
import type { InputActionBarConfig } from '../../components/InputActionBar/InputActionBar.types';
import type { FileUploadConfig } from '../../types/attachments';
import {
  mergeActionBarWithFileUpload,
  getFileUploadInputPropsFrom
} from '../../utils/fileUploadBarUtils';

export interface UseCommentFormFileUploadResult {
  enabled: true;
  fileUploadConfig: FileUploadConfig & {
    maxFiles: number;
    accept: string;
    allowMultipleFiles: boolean;
  };
  allowMultipleFiles: boolean;
  maxFiles: number;
  attachments: ReturnType<typeof useAttachments>['attachments'];
  attachmentCount: number;
  addFiles: ReturnType<typeof useAttachments>['addFiles'];
  removeFile: ReturnType<typeof useAttachments>['removeFile'];
  clearAttachments: ReturnType<typeof useAttachments>['clearAttachments'];
  getFiles: ReturnType<typeof useAttachments>['getFiles'];
  previewOpen: boolean;
  setPreviewOpen: React.Dispatch<React.SetStateAction<boolean>>;
  carouselOpen: boolean;
  setCarouselOpen: React.Dispatch<React.SetStateAction<boolean>>;
  carouselIndex: number;
  setCarouselIndex: React.Dispatch<React.SetStateAction<number>>;
  keepExpandedForUpload: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  uploadAreaRef: React.RefObject<HTMLDivElement | null>;
  triggerFileSelect: () => void;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearValueAndAttachments: () => void;
  openCarousel: (index: number) => void;
  registerRefocusInput: (fn: (() => void) | null) => void;
  /** Clear attachments and close preview/carousel after submit. */
  resetAfterSubmit: () => void;
}

/** Merges base action bar config with file-upload state (delegates to shared util for DRY). */
export function getActionBarConfigWithFileUpload(
  base: InputActionBarConfig | undefined,
  fileUpload: UseCommentFormFileUploadResult
): InputActionBarConfig {
  return mergeActionBarWithFileUpload(base, fileUpload);
}

/** Builds the file-upload slice for DivWithTextAreaSuggestions (delegates to shared util for DRY). */
export function getFileUploadInputProps(
  fileUpload: UseCommentFormFileUploadResult
) {
  return getFileUploadInputPropsFrom(fileUpload);
}

/**
 * Encapsulates all file-upload state and handlers for the comment form.
 * Call only when actionBarConfig.showUpload is true. Returns null when upload is disabled.
 */
export function useCommentFormFileUpload(
  actionBarConfig: InputActionBarConfig | undefined,
  clearValue: () => void
): UseCommentFormFileUploadResult | null {
  const showUpload = actionBarConfig?.showUpload === true;
  const fileUploadConfig = getFileUploadConfig(
    actionBarConfig?.fileUploadConfig
  );

  const allowMultipleFiles = fileUploadConfig.allowMultipleFiles !== false;
  const maxFiles = allowMultipleFiles ? fileUploadConfig.maxFiles ?? 10 : 1;

  const {
    attachments,
    addFiles,
    removeFile,
    clearAttachments,
    getFiles,
    attachmentCount
  } = useAttachments({ maxFiles });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [keepExpandedForUpload, setKeepExpandedForUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAreaRef = useRef<HTMLDivElement | null>(null);
  const refocusInputRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const onWindowFocus = () => setKeepExpandedForUpload(false);
    window.addEventListener('focus', onWindowFocus);
    return () => window.removeEventListener('focus', onWindowFocus);
  }, []);

  const triggerFileSelect = useCallback(() => {
    setKeepExpandedForUpload(true);
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files?.length) {
        addFiles(files, {
          maxSizePerFile: fileUploadConfig.maxSizePerFile
        });
      }
      e.target.value = '';
      setKeepExpandedForUpload(false);
      refocusInputRef.current?.();
    },
    [addFiles, fileUploadConfig.maxSizePerFile]
  );

  const clearValueAndAttachments = useCallback(() => {
    clearValue();
    clearAttachments();
    setPreviewOpen(false);
    setCarouselOpen(false);
  }, [clearValue, clearAttachments]);

  const registerRefocusInput = useCallback((fn: (() => void) | null) => {
    refocusInputRef.current = fn;
  }, []);

  const openCarousel = useCallback((index: number) => {
    setCarouselIndex(index);
    setCarouselOpen(true);
    setPreviewOpen(false);
  }, []);

  const resetAfterSubmit = useCallback(() => {
    clearAttachments();
    setPreviewOpen(false);
    setCarouselOpen(false);
  }, [clearAttachments]);

  if (!showUpload) return null;

  return {
    enabled: true as const,
    fileUploadConfig,
    allowMultipleFiles,
    maxFiles,
    attachments,
    attachmentCount,
    addFiles,
    removeFile,
    clearAttachments,
    getFiles,
    previewOpen,
    setPreviewOpen,
    carouselOpen,
    setCarouselOpen,
    carouselIndex,
    setCarouselIndex,
    keepExpandedForUpload,
    fileInputRef,
    uploadAreaRef,
    triggerFileSelect,
    handleFileInputChange,
    clearValueAndAttachments,
    openCarousel,
    registerRefocusInput,
    resetAfterSubmit
  };
}
